import { useState, useEffect, Suspense, type ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Notification } from "@/components/admin/notification";
import { Error } from "@/components/admin/error";
import { Skeleton } from "@/components/ui/skeleton";
import { useConfigurationLoader } from "../root/useConfigurationLoader";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { RealEstateProvider } from "../providers/RealEstateContext";

/**
 * App chrome: collapsible Sidebar + Header + main viewport.
 * Spec docs sometimes call this `AppLayout.tsx` — the real export is `Layout`
 * (wired as `<CRM layout={Layout} />`). Do not rename unless updating all imports.
 */
export const Layout = ({ children }: { children: ReactNode }) => {
  useConfigurationLoader();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem("re_sidebar_collapsed");
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    localStorage.setItem("re_sidebar_collapsed", JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  return (
    <RealEstateProvider>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        {/* Collapsible Left Sidebar */}
        <Sidebar isCollapsed={isCollapsed} onToggleCollapse={toggleSidebar} />

        {/* Main Right Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden min-w-0">
          {/* Utility Top Header */}
          <Header isCollapsed={isCollapsed} onToggleSidebar={toggleSidebar} />

          {/* Scrollable Viewport */}
          <main
            className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-900/50 p-6"
            id="main-content"
          >
            <ErrorBoundary FallbackComponent={Error}>
              <Suspense
                fallback={<Skeleton className="h-12 w-12 rounded-full" />}
              >
                {children}
              </Suspense>
            </ErrorBoundary>
          </main>
        </div>

        <Notification />
      </div>
    </RealEstateProvider>
  );
};

export default Layout;
